import { getAccessToken } from './auth';
import { buildOxrConfirmationPdf, formatDate } from './oxrConfirmationPdfLayout';

async function loadHeaderImage() {
  const response = await fetch(`${process.env.PUBLIC_URL || ''}/oxr-header.png`);
  if (!response.ok) {
    throw new Error('Failed to load OxR header image');
  }
  const blob = await response.blob();
  const dataUrl = await new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });

  const dims = await new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve({ width: img.naturalWidth, height: img.naturalHeight });
    img.onerror = reject;
    img.src = dataUrl;
  });

  return { dataUrl, dims };
}

/**
 * Builds the Overwatch x Rescue DTC confirmation letter.
 * Requires a valid MSAL access token — no query-param shortcut URL.
 * Pass a window opened synchronously from the click handler to avoid popup blockers.
 */
export async function openOxrConfirmationPdf(record, targetWindow = null) {
  try {
    const accessToken = await getAccessToken();
    if (!accessToken) {
      throw new Error('Authentication required to generate confirmation letter');
    }

    const oxrParents = ['OXR DTC', 'ONX MAPS'];
    if (!record || !oxrParents.includes(record.Parent)) {
      throw new Error('Confirmation letter is only available for OXR DTC and ONX MAPS members');
    }

    const username = `${record.First || ''} ${record.Last || ''}`.trim() || 'Member';
    const accountNumber = record.MembershipEXT || '';
    const effectiveDate = formatDate(record.Start);

    const { dataUrl: headerDataUrl, dims } = await loadHeaderImage();
    const doc = buildOxrConfirmationPdf({
      username,
      accountNumber,
      effectiveDate,
      headerDataUrl,
      headerDims: dims,
    });

    const blobUrl = doc.output('bloburl');
    if (targetWindow && !targetWindow.closed) {
      targetWindow.location = blobUrl;
    } else {
      const win = window.open(blobUrl, '_blank', 'noopener,noreferrer');
      if (!win) {
        doc.save('oxr_confirmation.pdf');
      }
    }
  } catch (error) {
    if (targetWindow && !targetWindow.closed) {
      targetWindow.close();
    }
    throw error;
  }
}
